import React from 'react';

import {
  Close as CloseIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  Download as DownloadIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import {
  Box,
  FormControl,
  FormLabel,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  listItemSecondaryActionClasses,
  Typography,
} from '@mui/material';
import { isNotNil } from 'es-toolkit';
import { isEmpty } from 'es-toolkit/compat';
import { type Accept, useDropzone } from 'react-dropzone';
import type { FieldInputProps } from 'react-final-form';

import type { Nullable } from '@hrlogics/helper-types';

import { filePickerFileTypes } from '@clearverify/constants';

import { validateMaxTotalFilesSize } from 'src/components/finalForm/DropzoneFilesField/utils/validate';
import { DEFAULT_FILE_TYPES } from 'src/constants/file';
import { useScreenInfo } from 'src/hooks/common';
import { useFileUpload } from 'src/hooks/file';
import type { DroppedFile, FilePickerFileTypesKeys } from 'src/types/file';
import type { SxProp } from 'src/types/mui';

import { FileListItemStatus } from './FileListItemStatus';
import type { DroppedFilesValidationFunction } from './types';

type DropzoneFilesFieldProps = {
  ref?: React.ForwardedRef<unknown>;
  input: FieldInputProps<Nullable<DroppedFile[]>>;
  customChildren?: React.ReactNode;
  label?: React.ReactNode;
  dropzoneLabel?: React.ReactNode;
  maxFiles?: number;
  disabled?: boolean;
  acceptFileTypes?: FilePickerFileTypesKeys[];
  immediateUpload?: boolean;
  hideAttachedFiles?: boolean;
  validation?: DroppedFilesValidationFunction;
  onError?: () => void;
  showDropzone?: boolean;
  maxTotalFilesSize?: number;
};

export const DropzoneFilesField: React.FC<DropzoneFilesFieldProps> = ({
  ref,
  input,
  customChildren,
  label,
  dropzoneLabel = 'Drop files here or browse',
  maxFiles = 5,
  maxTotalFilesSize,
  disabled = false,
  acceptFileTypes = DEFAULT_FILE_TYPES,
  immediateUpload = true,
  hideAttachedFiles = false,
  validation,
  onError,
}) => {
  const { name, value, onChange: setFilesList } = input;

  const { uploadFiles } = useFileUpload({ skip: !immediateUpload });
  const { isMobile } = useScreenInfo();

  const filesList = React.useMemo(() => value || [], [value]);

  const onFilesDrop = React.useCallback(
    async (acceptedFiles: File[]) => {
      const isAsyncValidation = validation?.constructor.name === 'AsyncFunction';

      const oldFiles = filesList;
      let newFiles: DroppedFile[] = acceptedFiles.map(file => ({
        id: window.crypto.randomUUID(),
        file,
        size: file.size,
        ...(validation
          ? { valid: false, validationComplete: false, validationInProgress: isAsyncValidation }
          : {}),
      }));

      if (!validateMaxTotalFilesSize(maxTotalFilesSize, [...oldFiles, ...newFiles]) && onError) {
        onError();
        return;
      }

      // shortcut
      const saveFiles = () => setFilesList([...oldFiles, ...newFiles]);

      if (validation) {
        // make a save to show the status
        if (isAsyncValidation) saveFiles();

        const validatedFiles = (await validation(newFiles)) || [];

        // map files for validation
        newFiles = newFiles.map(file => {
          const invalidFile = validatedFiles.find(
            invalidFile => invalidFile.fileId === file.id && !invalidFile.valid,
          );

          const statuses: Partial<DroppedFile> = {
            validationInProgress: false,
            validationComplete: true,
          };

          if (invalidFile) {
            return { ...file, ...statuses, valid: false, errorMessage: invalidFile.message };
          }

          return { ...file, ...statuses, valid: true };
        });

        // make a save to show the status
        saveFiles();
      }

      if (immediateUpload) {
        // map files for uploading
        newFiles = newFiles.map(fileInfo => ({
          ...fileInfo,
          ...(validation && fileInfo.valid ? { loading: true, loadingComplete: false } : {}),
          ...(!validation ? { loading: true, loadingComplete: false } : {}),
        }));

        // make a save to show the status
        saveFiles();

        const filesToUpload = newFiles
          .filter(fileInfo => fileInfo.loading)
          .map(fileInfo => fileInfo.file)
          .filter(isNotNil);

        // process the case if all files are invalid
        if (filesToUpload.length === 0) {
          newFiles = newFiles.map(fileInfo => {
            return {
              ...fileInfo,
              loading: false,
              loadingComplete: false,
              errorMessage: fileInfo.errorMessage ?? 'Validation Error. Please try again',
            };
          });
        } else {
          const uploadedFiles = await uploadFiles(
            filesToUpload.map(fileItem => ({ file: fileItem })),
          );

          newFiles = newFiles.map(fileInfo => {
            const uploadedFile = uploadedFiles.find(
              file => file.size === fileInfo.file?.size && file.filename === fileInfo.file.name,
            );
            return {
              ...fileInfo,
              loading: false,
              loadingComplete: true,
              uploadedFileId: uploadedFile?.id,
              uploadedFileUrl: uploadedFile?.downloadUrl,
              uploadedFileName: uploadedFile?.filename,
            };
          });
        }
      }

      saveFiles();
    },
    [filesList, immediateUpload, maxTotalFilesSize, onError, uploadFiles, setFilesList, validation],
  );

  const removeFileFromList = React.useCallback(
    (fileId: string) => {
      const newFilesList = filesList.filter(file => file.id !== fileId);
      setFilesList(newFilesList);
    },
    [filesList, setFilesList],
  );

  const acceptFiles = React.useMemo(
    () =>
      acceptFileTypes.reduce(
        (acc: Accept, fileType) => ({ ...acc, ...filePickerFileTypes[fileType] }),
        {},
      ),
    [acceptFileTypes],
  );

  const dropzoneMaxFiles = maxFiles - filesList.length;
  const dropzoneDisabled = disabled || dropzoneMaxFiles === 0;

  const { getRootProps, getInputProps, open } = useDropzone({
    useFsAccessApi: false,
    maxFiles: dropzoneMaxFiles,
    disabled: dropzoneDisabled,
    onDrop: onFilesDrop,
    accept: acceptFiles,
  });

  // pass ref if you want trigger file picker manually
  React.useImperativeHandle(ref, () => ({
    open,
  }));

  if (customChildren) {
    return (
      <Box data-e2e-element="file-dropzone-input" {...getRootProps()}>
        <input name={name} {...getInputProps()} />
        {customChildren}
      </Box>
    );
  }

  return (
    <React.Fragment>
      <FormControl fullWidth disabled={dropzoneDisabled}>
        <React.Activity mode={label ? 'visible' : 'hidden'}>
          <Typography variant="body1" component={FormLabel}>
            {label}
          </Typography>
        </React.Activity>
        {!ref && (
          <Box
            sx={dropzoneBoxStyles(dropzoneDisabled)}
            data-e2e-element="file-dropzone-input"
            {...getRootProps()}
          >
            <input name={name} {...getInputProps()} />

            <Box sx={dropzoneInternalBoxStyles}>
              <UploadFileIcon color="primary" fontSize="large" />
              <Typography variant="body1">{dropzoneLabel}</Typography>
            </Box>
          </Box>
        )}
      </FormControl>

      <React.Activity mode={!isEmpty(filesList) && !hideAttachedFiles ? 'visible' : 'hidden'}>
        <Typography variant="body1" mt={2}>
          Uploaded {filesList.length} file(-s)
        </Typography>

        <List sx={listStyles}>
          {filesList.map(fileInfo => (
            <ListItem
              key={fileInfo.id}
              secondaryAction={
                <React.Fragment>
                  {!isMobile && fileInfo.uploadedFileUrl && (
                    <IconButton
                      size="large"
                      href={fileInfo.uploadedFileUrl}
                      target="_blank"
                      disabled={fileInfo.loading}
                    >
                      <DownloadIcon />
                    </IconButton>
                  )}
                  <IconButton
                    size="large"
                    onClick={() => removeFileFromList(fileInfo.id)}
                    disabled={fileInfo.loading}
                    color="error"
                  >
                    <CloseIcon />
                  </IconButton>
                </React.Fragment>
              }
            >
              <ListItemAvatar sx={{ alignItems: 'center', justifyContent: 'center' }}>
                <DescriptionOutlinedIcon fontSize="large" />
              </ListItemAvatar>

              <ListItemText
                primary={fileInfo.file?.name || fileInfo.uploadedFileName}
                secondary={
                  <FileListItemStatus
                    fileInfo={fileInfo}
                    validationEnabled={!!validation}
                    uploadingEnabled={immediateUpload}
                  />
                }
              />
            </ListItem>
          ))}
        </List>
      </React.Activity>
    </React.Fragment>
  );
};

const dropzoneBoxStyles = (disabled: boolean) => {
  const styles: SxProp = theme => ({
    mt: 1,
    border: '2px dashed rgba(0, 0, 0, 0.12)',
    borderRadius: '4px',
    opacity: disabled ? 0.25 : undefined,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: theme.transitions.easing.easeInOut,
    '&:hover': disabled ? undefined : { backgroundColor: 'rgba(0, 0, 0, 0.06)' },
  });
  return styles;
};

const dropzoneInternalBoxStyles: SxProp = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: 160,
  gap: 2,
};

const listStyles: SxProp = {
  [`.${listItemSecondaryActionClasses.root}`]: {
    display: 'flex',
    gap: 1,
  },
};
